import React, { useId, useMemo } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import { MilestoneDetails } from './SellerPlanPayback';
import {
  PAYBACK_COPY,
  PAYBACK_HELP_PATH,
  createPaybackClient,
  formatCycleDate,
  formatCycleRange,
  normalizePayback,
} from './sellerPlanPayback';
import useSellerPlanPayback from './useSellerPlanPayback';
import './SellerPlanPayback.css';

/**
 * Lasting billing-dashboard card for the latest earned milestone. It stays after dismissal and
 * after the cycle ends, labelled with its dates; only the popup is limited to the active cycle.
 *
 * Two uses, one render path:
 * - inside PlanBillingPage: pass the raw `data.payback` from its billing read as `payback`
 *   (no second request);
 * - on its own mount (legacy plan page): omit `payback` and the card reads the snapshot itself.
 * Nonqualifiers, an absent field, an old server and an unsupported version all render nothing.
 */
export default function SellerPlanPaybackCard({
  payback: provided,
  client: clientOverride,
  channelId: channelOverride,
}) {
  const auth = useAuth();
  const standalone = provided === undefined;
  const client = useMemo(
    () => (standalone
      ? clientOverride || createPaybackClient({ fetch: auth.fetch, apiUrl: auth.API_URL })
      : null),
    [standalone, clientOverride, auth.fetch, auth.API_URL],
  );
  const channelId = channelOverride === undefined ? (auth.channel?.id ?? null) : channelOverride;
  const read = useSellerPlanPayback({ client, channelId, autoClaim: false });
  const embedded = useMemo(() => normalizePayback(provided), [provided]);
  const view = standalone ? read.payback : embedded;
  const titleId = useId();

  const milestone = view?.milestone;
  if (!milestone) return null;
  const active = milestone.isActiveCycle;

  return (
    <div className="sppb-card" role="region" aria-labelledby={titleId}>
      <p className="sppb-eyebrow">Subscription payback</p>
      <h2 id={titleId}>{active ? PAYBACK_COPY.headline : PAYBACK_COPY.historyHeadline}</h2>
      <p className="sppb-meta">
        Billing cycle: {formatCycleRange(milestone)}. Reached on {formatCycleDate(milestone.earnedAt)}.
      </p>
      <p>{active ? PAYBACK_COPY.body : PAYBACK_COPY.historyBody}</p>
      {!active && (
        <p className="sppb-meta">
          This is the latest milestone you reached. It stays here as a record of that billing cycle.
        </p>
      )}
      <MilestoneDetails milestone={milestone} />
      <a className="sppb-help" href={view.helpPath || PAYBACK_HELP_PATH} target="_blank" rel="noopener">
        Learn more
      </a>
    </div>
  );
}
